import { useEffect, useState } from 'react';
import { differenceInDays } from 'date-fns';

interface DayCounterProps {
  startDate: string;
  endDate: string;
}

function DayCounter({ startDate, endDate }: DayCounterProps) {
  const [currentDay, setCurrentDay] = useState(1);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const day = Math.min(90, Math.max(1, differenceInDays(new Date(), new Date(startDate)) + 1));
    
    if (isAnimating) {
      let current = 1;
      const interval = setInterval(() => {
        if (current >= day) {
          setIsAnimating(false);
          clearInterval(interval);
        }
        setCurrentDay(current);
        current += 1;
      }, 20);

      return () => clearInterval(interval);
    } else {
      setCurrentDay(day);
    }
  }, [startDate, isAnimating]);

  const progress = (currentDay / 90) * 100;
  const circumference = 2 * Math.PI * 120;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const milestones = [30, 45, 60, 90];

  return (
    <div className="relative w-[280px] h-[280px]">
      <svg className="w-full h-full transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="140"
          cy="140"
          r="120"
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="12"
        />
        {/* Progress circle */}
        <circle
          cx="140"
          cy="140"
          r="120"
          fill="none"
          stroke="#92af67"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-300 ease-out"
        />
        
        {/* Milestone markers */}
        {milestones.map((day) => {
          const angle = (day / 90) * 360 - 90;
          const rad = (angle * Math.PI) / 180;
          const markerX = 140 + 120 * Math.cos(rad);
          const markerY = 140 + 120 * Math.sin(rad);
          
          return (
            <circle
              key={day}
              cx={markerX}
              cy={markerY}
              r="4"
              fill={currentDay >= day ? '#92af67' : '#e5e7eb'}
              className="transition-colors duration-300"
            />
          );
        })}
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-5xl font-bold text-gray-900 transition-all duration-200">
          {currentDay}
        </div>
        <div className="text-sm text-gray-500 mt-1">
          of 90 days
        </div>
        <div className="text-sm font-medium text-gray-600 mt-2">
          {Math.round(progress)}% Complete
        </div>
      </div>
    </div>
  );
}

export default DayCounter;